import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    ChangeDetectorRef,
    NgZone
} from '@angular/core';
import { isPanelClosable, PanelModel } from '../../panel.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as shortid from 'shortid';
import { MessageBusService } from 'projects/shared-components/message-bus.service';

interface TabWidthData {
    tab: PanelModel;
    width: number;
}

@Component({
    selector: 'ets-tabs-panel',
    templateUrl: './tabs-panel.component.html',
    styleUrls: ['./tabs-panel.component.scss']
})
export class EtsTabsPanelComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('panel', { static: true })
    private panelElement: ElementRef<HTMLElement>;

    @Input() tabs: PanelModel[];
    @Input() selectedTabId: string; // Optional: auto-scroll to this tab when it changes
    @Output() tabClicked = new EventEmitter<PanelModel>();
    @Output() tabClosed = new EventEmitter<PanelModel>();
    @Output() symbolLinkChanged = new EventEmitter<any>();
    @Output() tabAdded = new EventEmitter<PanelModel>();
    @Output() tabHeaderChanged = new EventEmitter<PanelModel>();

    wspId: string;
    leftPaddleVisible = false;
    rightPaddleVisible = false;

    private readonly TAB_MARGIN = 20;
    private readonly SCROLL_AMOUNT = 200; // pixels to scroll on paddle click
    private tabSizes: TabWidthData[] = [];
    private unsubscriber = new Subject<void>();
    private resizeObserver: ResizeObserver;
    private updatePaddlesTimeout: any;

    constructor(
        private messageBus: MessageBusService,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) {
        this.tabs = [];
    }

    ngOnInit() {
        this.wspId = shortid.generate();

        this.messageBus
            .of<any>('PanelSelfCloseRequest')
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(x => {
                const tab = this.tabSizes.find(t => t.tab.panelId === x.payload.tabId);
                if (tab && isPanelClosable(tab.tab)) {
                    this.onTabCloseRequest(tab.tab);
                }
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        // Watch for selectedTabId changes and auto-scroll to it
        if (changes['selectedTabId'] && !changes['selectedTabId'].firstChange) {
            const newTabId = changes['selectedTabId'].currentValue;
            if (newTabId) {
                setTimeout(() => this.scrollToTab(newTabId), 100);
            }
        }
    }

    ngAfterViewInit() {
        // Initialize resize observer to handle container resizing
        this.ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver(() => {
                this.ngZone.run(() => this.updatePaddleVisibility());
            });

            if (this.panelElement?.nativeElement) {
                this.resizeObserver.observe(this.panelElement.nativeElement);
            }
        });

        // Initial paddle visibility check
        setTimeout(() => this.updatePaddleVisibility(), 100);
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        if (this.updatePaddlesTimeout) {
            clearTimeout(this.updatePaddlesTimeout);
        }
    }

    // ==================== Event Handlers ====================

    onTabAdded($event: { tab: PanelModel; newWidth: number }) {
        if (!$event.newWidth) {
            return;
        }

        const tabWidth = $event.newWidth + this.TAB_MARGIN;
        this.tabSizes.push({ tab: $event.tab, width: tabWidth });

        this.tabAdded.emit($event.tab);

        // Scroll to the newly added tab and update paddles
        setTimeout(() => {
            this.scrollToTab($event.tab.panelId);
            this.updatePaddleVisibility();
        }, 50);
    }

    onTabClicked(tab: PanelModel): void {
        this.tabClicked.emit(tab);
        // Auto-scroll to clicked tab if it's not fully visible
        this.scrollToTab(tab.panelId);
    }

    onTabHeaderChanged(tab: PanelModel): void {
        this.tabHeaderChanged.emit(tab);
    }

    onTabCloseRequest(tab: PanelModel): void {
        if (!isPanelClosable(tab)) {
            return;
        }
        this.tabClosed.emit(tab);

        // Remove tab from size tracking
        const index = this.tabSizes.findIndex(x => x.tab === tab);
        if (index >= 0) {
            this.tabSizes.splice(index, 1);
        }

        // Update paddle visibility after tab removal
        setTimeout(() => this.updatePaddleVisibility(), 50);
    }

    onSymbolLinkChanged(ev: any, tab: PanelModel) {
        this.symbolLinkChanged.emit({ isLinked: ev, panel: tab });
    }

    onLeftPaddleClick(): void {
        this.scrollPanel(-this.SCROLL_AMOUNT);
    }

    onRightPaddleClick(): void {
        this.scrollPanel(this.SCROLL_AMOUNT);
    }

    // ==================== Scroll Methods ====================

    private scrollPanel(distance: number): void {
        const panel = this.panelElement.nativeElement;
        const targetScroll = panel.scrollLeft + distance;

        panel.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
        });

        // Update paddle visibility after scroll animation completes
        this.scheduleUpdatePaddles(400);
    }

    /**
     * Scrolls to a specific tab by its panel ID.
     * Can be called programmatically from parent component.
     * @param panelId The ID of the tab to scroll to
     */
    public scrollToTab(panelId: string): void {
        const panel = this.panelElement.nativeElement;
        const tabIndex = this.tabs.findIndex(t => t.panelId === panelId);

        if (tabIndex === -1) {
            return;
        }

        // Calculate the position of the tab
        let tabPosition = 0;
        for (let i = 0; i < tabIndex && i < this.tabSizes.length; i++) {
            tabPosition += this.tabSizes[i].width;
        }

        const tabWidth = this.tabSizes[tabIndex]?.width || 0;
        const panelWidth = panel.offsetWidth;
        const currentScroll = panel.scrollLeft;

        // Determine if we need to scroll
        const tabStart = tabPosition;
        const tabEnd = tabPosition + tabWidth;
        const visibleStart = currentScroll;
        const visibleEnd = currentScroll + panelWidth;

        let targetScroll = currentScroll;

        if (tabEnd > visibleEnd) {
            // Tab is to the right, scroll right to show it
            targetScroll = tabEnd - panelWidth + 50; // 50px padding
        } else if (tabStart < visibleStart) {
            // Tab is to the left, scroll left to show it
            targetScroll = tabStart - 50; // 50px padding
        } else {
            // Tab is already visible, no need to scroll
            return;
        }

        panel.scrollTo({
            left: Math.max(0, targetScroll),
            behavior: 'smooth'
        });

        this.scheduleUpdatePaddles(400);
    }

    /**
     * Scrolls to a tab by its index in the tabs array.
     * @param index The index of the tab to scroll to
     */
    public scrollToTabByIndex(index: number): void {
        if (index >= 0 && index < this.tabs.length) {
            this.scrollToTab(this.tabs[index].panelId);
        }
    }

    // ==================== Paddle Visibility ====================

    private updatePaddleVisibility(): void {
        const panel = this.panelElement?.nativeElement;
        if (!panel) {
            return;
        }

        const totalTabsWidth = this.getTotalTabsWidth();
        const panelWidth = panel.offsetWidth;

        // Check if scrolling is needed at all
        if (totalTabsWidth <= panelWidth) {
            this.leftPaddleVisible = false;
            this.rightPaddleVisible = false;
            this.cdr.detectChanges();
            return;
        }

        // Check if we can scroll left
        this.leftPaddleVisible = panel.scrollLeft > 5; // 5px threshold

        // Check if we can scroll right
        const maxScroll = panel.scrollWidth - panel.offsetWidth;
        this.rightPaddleVisible = panel.scrollLeft < maxScroll - 5; // 5px threshold

        this.cdr.detectChanges();
    }

    private scheduleUpdatePaddles(delay: number): void {
        if (this.updatePaddlesTimeout) {
            clearTimeout(this.updatePaddlesTimeout);
        }

        this.updatePaddlesTimeout = setTimeout(() => {
            this.updatePaddleVisibility();
        }, delay);
    }

    // ==================== Helper Methods ====================

    private getTotalTabsWidth(): number {
        return this.tabSizes.reduce((total, tab) => total + tab.width, 0);
    }
}
